import {
  TWO_FACTOR_TOTP_FORM_ID
} from "./chunk-Y6C2WA2G.mjs";

// src/internal/components/two-factor-panel/two-factor-totp-form.tsx
import * as React from "react";
import { Controller, useForm } from "react-hook-form";
import { object, string } from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import { useAuthContext } from "@clara/auth";
import { useT } from "@clara/locale";
import { Banner } from "@clara/ui";
import { omit, runIfFn } from "@clara/utils";
import { jsx, jsxs } from "react/jsx-runtime";
var TOTP_PATTERN = new RegExp(/([0-9]{6})|([0-9a-fa-f]{5}-?[0-9a-fa-f]{5})/);
var TOP_RECOVERY_PATTERN = new RegExp(/([a-zA-Z0-9]+-)+[a-zA-Z0-9]+/);
var TOTP_BOTH_PATTERN = new RegExp(
  TOTP_PATTERN.source + "|" + TOP_RECOVERY_PATTERN.source
);
var OneTimePasswordSchema = object().shape({
  totp: string().required("fields.required.error").when("challenge", {
    is: (x) => x === "both",
    then: string().matches(TOTP_BOTH_PATTERN, "fields.both.error"),
    otherwise: string().when("challenge", {
      is: (x) => x === "recovery",
      then: string().matches(TOP_RECOVERY_PATTERN, "fields.recovery.error"),
      otherwise: string().matches(TOTP_PATTERN, "field.totp.error")
    })
  })
});
var TwoFactorTOTPForm = ({
  loading,
  error,
  challenge = "totp",
  onSubmit: onSubmitProp,
  ...props
}) => {
  const {
    control,
    handleSubmit,
    formState: { isValid }
  } = useForm({
    defaultValues: { totp: "", challenge },
    resolver: yupResolver(OneTimePasswordSchema),
    mode: "onChange"
  });
  const { t } = useT(["auth", "common"]);
  const { setPanelFormData } = useAuthContext();
  const children = runIfFn(props.children, { isValid, loading, error });
  React.useEffect(() => {
    setPanelFormData({
      formId: TWO_FACTOR_TOTP_FORM_ID,
      payload: {
        isValid,
        loading
      }
    });
  }, [loading, isValid]);
  return /* @__PURE__ */ jsxs(
    Stack,
    {
      id: TWO_FACTOR_TOTP_FORM_ID,
      component: "form",
      onSubmit: handleSubmit(
        (args) => onSubmitProp == null ? void 0 : onSubmitProp(omit(args, ["challenge"]))
      ),
      spacing: 3,
      ...props,
      children: [
        /* @__PURE__ */ jsx(
          Controller,
          {
            control,
            name: "totp",
            render: ({ field, fieldState: { error: error2 } }) => /* @__PURE__ */ jsx(
              TextField,
              {
                ...field,
                label: t(`fields.${challenge}.label`),
                variant: "outlined",
                error: !!error2,
                helperText: t(error2 == null ? void 0 : error2.message),
                size: "small",
                inputProps: {
                  ...challenge === "totp" && {
                    inputMode: "numeric"
                  }
                }
              }
            )
          }
        ),
        !!error && /* @__PURE__ */ jsx(Banner, { status: "critical", children: t("twofactor.totp.error") }),
        children
      ]
    }
  );
};

export {
  OneTimePasswordSchema,
  TwoFactorTOTPForm
};
